/* Shared product text interpolation and native same-product country selection. */
(() => {
  'use strict';

  const isObject = (value) => value && typeof value === 'object' && !Array.isArray(value);
  const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

  function createText(page, fallbackDictionary = {}) {
    let dictionary = {};
    try {
      const parsed = JSON.parse(page?.querySelector('[data-i18n]')?.textContent || '{}');
      dictionary = isObject(parsed?.strings) ? parsed.strings : isObject(parsed) ? parsed : {};
    } catch { /* A supplied German dictionary remains usable if page JSON is invalid. */ }
    const fallback = isObject(fallbackDictionary) ? fallbackDictionary : {};
    return (key, values = {}) => {
      const source = own(dictionary, key) && typeof dictionary[key] === 'string' ? dictionary[key]
        : own(fallback, key) && typeof fallback[key] === 'string' ? fallback[key] : String(key);
      const replacements = isObject(values) ? values : {};
      return source.replace(/\{([a-zA-Z][a-zA-Z0-9_]*)\}/g, (token, name) => own(replacements, name) ? String(replacements[name]) : token);
    };
  }

  if (!window.JblText?.create) window.JblText = { create: createText };
  if (window.JblLocalization?.init) { window.JblLocalization.init(); return; }

  const instances = new WeakMap();

  function createPicker(page, picker) {
    if (instances.has(picker)) return instances.get(picker);
    const toggle = picker.querySelector('[data-jbl-country-toggle]');
    const menu = picker.querySelector('[data-jbl-country-menu]');
    const forms = [...picker.querySelectorAll('[data-jbl-country-form]')];
    if (!toggle || !menu) return null;
    const desktopCountryControls = window.matchMedia('(min-width:1068px) and (hover:hover) and (pointer:fine)');
    const options = () => [...menu.querySelectorAll('[data-jbl-country-option]')].filter((option) => !option.disabled);

    function refreshReturns() {
      const variant = String(page.dataset.variant || '').trim();
      const currentUrl = new URL(location.href);
      forms.forEach((form) => {
        const input = form.querySelector('input[name="return_to"]');
        if (!input?.value) return;
        try {
          const destination = new URL(input.value, location.origin);
          if (destination.origin !== location.origin) return;
          if (variant) destination.searchParams.set('variant', variant);
          const country = form.querySelector('[name="country_code"]')?.value;
          const language = form.querySelector('[name="language_code"]')?.value;
          if (!destination.searchParams.has('country') && country) destination.searchParams.set('country', country);
          if (!destination.searchParams.has('lang') && language) destination.searchParams.set('lang', language);
          const preview = currentUrl.searchParams.get('preview_theme_id');
          if (preview) destination.searchParams.set('preview_theme_id', preview);
          destination.hash = location.hash;
          input.value = destination.pathname + destination.search + destination.hash;
        } catch { /* Preserve the native Liquid return URL if URL parsing is unavailable. */ }
      });
    }

    function close(restoreFocus = false) {
      menu.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      options().forEach((option) => { option.tabIndex = -1; });
      if (restoreFocus && desktopCountryControls.matches && toggle.getClientRects().length) toggle.focus({ preventScroll: true });
    }

    function open(focusTarget = 'selected') {
      if (!desktopCountryControls.matches) { close(); return; }
      refreshReturns();
      menu.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      const items = options();
      const target = focusTarget === 'first' ? items[0] : focusTarget === 'last' ? items[items.length - 1]
        : items.find((option) => option.getAttribute('aria-checked') === 'true') || items[0];
      items.forEach((option) => { option.tabIndex = option === target ? 0 : -1; });
      target?.focus({ preventScroll: true });
      target?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    }

    function onToggle() { if (menu.hidden) open(); else close(); }

    function onKeyDown(event) {
      if (!desktopCountryControls.matches) { close(); return; }
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'Escape' && !menu.hidden) {
        event.preventDefault();
        event.stopPropagation();
        close(true);
        return;
      }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      event.stopPropagation();
      if (menu.hidden) {
        open(event.key === 'ArrowUp' || event.key === 'End' ? 'last' : 'first');
        return;
      }
      const items = options();
      if (!items.length) return;
      const index = items.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
        : (index + (event.key === 'ArrowUp' ? -1 : 1) + items.length) % items.length;
      items.forEach((option, itemIndex) => { option.tabIndex = itemIndex === next ? 0 : -1; });
      items[next].focus({ preventScroll: true });
      items[next].scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    }

    function onOutsidePointer(event) { if (!picker.contains(event.target)) close(); }
    function onFocusOut(event) {
      if (event.relatedTarget) { if (!picker.contains(event.relatedTarget)) close(); }
      else queueMicrotask(() => { if (!picker.contains(document.activeElement)) close(); });
    }
    function onDesktopChange() { if (!desktopCountryControls.matches && !menu.hidden) close(); }

    toggle.addEventListener('click', onToggle);
    picker.addEventListener('keydown', onKeyDown);
    picker.addEventListener('focusout', onFocusOut);
    document.addEventListener('pointerdown', onOutsidePointer);
    forms.forEach((form) => form.addEventListener('submit', refreshReturns));
    window.addEventListener('hashchange', refreshReturns);
    desktopCountryControls.addEventListener('change', onDesktopChange);
    onDesktopChange();
    refreshReturns();
    const controller = {
      refresh: refreshReturns,
      close,
      destroy() {
        close();
        toggle.removeEventListener('click', onToggle);
        picker.removeEventListener('keydown', onKeyDown);
        picker.removeEventListener('focusout', onFocusOut);
        document.removeEventListener('pointerdown', onOutsidePointer);
        forms.forEach((form) => form.removeEventListener('submit', refreshReturns));
        window.removeEventListener('hashchange', refreshReturns);
        desktopCountryControls.removeEventListener('change', onDesktopChange);
        instances.delete(picker);
      }
    };
    instances.set(picker, controller);
    return controller;
  }

  function init(container = document) {
    if (!container?.querySelectorAll) return [];
    const pages = container.matches?.('[data-jbl-page]') ? [container] : [...container.querySelectorAll('[data-jbl-page]')];
    return pages.flatMap((page) => [...page.querySelectorAll('[data-jbl-country-picker]')].map((picker) => createPicker(page, picker)).filter(Boolean));
  }

  window.JblLocalization = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
